import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { IHandleResponseError } from "./IHandleResponseError";

/**
 * An error handler which logs errors and warnings in responses.
 */
class ResponseIssueConsoleLogger implements IHandleResponseError {
    /**
     * @inheritdoc
     */
    public handleError(response: ServerEvent): boolean {
        const hasErrors = response?.hasErrors();
        const hasWarnings = response?.hasWarnings();

        if (hasErrors || hasWarnings) {
            const label = this.getOperationLabel(response);
            this.logErrors(response, label);
            this.logWarnings(response, label);
        }

        return true;
    }

    private getOperationLabel(response: ServerEvent): string {
        let label = EnumHelper.getName(ServerOperations.Server, response.Operation);
        if (response.MessageId)
            label += ` (request ID ${response.MessageId})`;
        return label;
    }

    private logErrors(response: ServerEvent, label: string): void {
        const errorsToLog = response.Operation === ServerOperations.Server.Myle_Scheduling_UpdateRuleSet
            ? response.Errors?.filter(error => error.Message !== "The rule set overlaps with existing rule sets.")
            : response.Errors;

        if (response.hasErrors() && errorsToLog?.length)
            console.error(`Error${errorsToLog.length > 1 ? "s" : ""} handling ${label}:\n${this.FormatErrorList(errorsToLog)}`, { response });
    }

    private logWarnings(response: ServerEvent, label: string): void {
        if (response.hasWarnings())
            console.warn(`Warning${response.Warnings.length > 1 ? "s" : ""} handling ${label}:\n${this.FormatErrorList(response.Warnings)}`, { response });
    }

    /**
     * Format a list of errors as human-readable text.
     * @param label The label for the message, like 'Error' or 'Warning'.
     * @param errors The error messages to list.
     * @returns The formatted error list.
     */
    private FormatErrorList(errors: ErrorMessage[]): string {
        let message = "";
        for (const error of errors)
            message += `- ${this.FormatError(error)}\n`;
        return message;
    }

    /**
     * Format an error as human-readable text.
     * @param label The label for the message, like 'Error' or 'Warning'.
     * @param errors The error messages to list.
     * @returns The formatted error list.
     */
    private FormatError(error: ErrorMessage): string {
        let message = error.Message || "<unknown error>";
        if (error.Code)
            message = `[${error.Code}] ${message}`;
        return message;
    }
}

export { ResponseIssueConsoleLogger };
